import { isUserAuthenticated } from "@/middleware/auth";
import {
  addChatToLocalDB,
  getChatFromLocalDB,
  removeChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import { MemberRole } from "@/server/types/member";
import { SocketEventActionType, ChatMemberEventType } from "./types";

import { useNavigationState } from "@/zustand/NavigationState";
import { navigateToChatList } from "@/lib/navigationRef";

import { ChatEntity, InitChatStatus } from "@/indexedDB/types";
import { ChatType } from "@/server/types/chat";
import { queryClient } from "@/queryClient";
import { removeChatDataFromLocalDB } from "@/indexedDB/services/chatTeardown";

export const handleChatMemberEvent = async (data: ChatMemberEventType) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  switch (data.action) {
    case SocketEventActionType.CREATE:
      await onAddMemberToChat({ eventPayload: data });
      break;
    case SocketEventActionType.UPDATE:
      await onUpdateChatMember({ eventPayload: data });
      break;
    case SocketEventActionType.DELETE:
      await onRemoveMemberFromChat({ eventPayload: data });
      break;

    default:
      break;
  }
};

/**
 * `chat-member` Update — a member's ROLE changed.
 *
 * NEW USAGE with account deletion (BE handoff §4.1/§6): when a deleted
 * user's Architect chat still has another human in it, the backend promotes
 * the OA bot to Owner and announces it with THIS event (deliberately
 * "chat-member", not the Rust transfer flow's "member" event, which delta
 * sync cannot parse). Mirrors memberEventsHandler's "member" Update logic —
 * the two events carry different payload shapes but mean the same thing;
 * both handlers are idempotent so double delivery is harmless.
 *
 * The invalidation is UNCONDITIONAL (not only for the active chat):
 * succession happens in chats the viewer is not looking at, and a stale
 * cached member list would keep showing the deleted user as Owner.
 */
const onUpdateChatMember = async ({
  eventPayload,
}: {
  eventPayload: ChatMemberEventType;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  const chatId = eventPayload.chat_member.chat.id;
  const member = eventPayload.chat_member.member;
  const isCurrentUser = userFingerPrint.username === member.username;

  if (isCurrentUser) {
    // Our own role changed — persist it; every permission gate reads it.
    await updateChatInLocalDB(chatId, {
      roleAsMember: {
        username: member.username,
        role: member.role,
      },
    });
    if (useNavigationState.getState().activeChatId === chatId) {
      useNavigationState.getState().setActiveChatRoleAsMember(member.role);
    }
  }

  queryClient.invalidateQueries({ queryKey: ["chat-members", chatId] });
  queryClient.invalidateQueries({ queryKey: ["search-members"] });
};

const onAddMemberToChat = async ({
  eventPayload,
}: {
  eventPayload: ChatMemberEventType;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  const activeChatId = useNavigationState.getState().activeChatId;

  const setActiveChatMemberCount =
    useNavigationState.getState().setActiveChatMemberCount;
  // const setActiveChatRemovedRoleAsMember =
  //   useNavigationState.getState().setActiveChatRemovedRoleAsMember;

  const memberName = eventPayload.chat_member.member.username;
  const memberRole = eventPayload.chat_member.member.role;
  const memberCount = eventPayload.chat_member.chat.member_count;
  const chatId = eventPayload.chat_member.chat.id;
  const isCurrentUser = userFingerPrint.username === memberName;
  const localDBChat = await getChatFromLocalDB(chatId);

  if (isCurrentUser) {
    // if chat already exists in local db
    // handle scenarios where user has been removed
    // and added back before the user deteled the chat from local db/state
    // update chat in local db with new member role as NOT removed

    if (localDBChat) {
      // Process pinned messages for existing chat
      // usePinnedMessageState.getState().setPinnedMessageIdsForChat(
      //   chatId,
      //   eventPayload.chat_member.pinned_messages.map((m) => ({
      //     id: m.message_id,
      //     timestamp: m.message.sent_at,
      //   })),
      // );

      await updateChatInLocalDB(chatId, {
        memberCount: memberCount,
        roleAsMember: {
          username: memberName,
          role: memberRole,
          removed: false,
        },
      });

      // if chat id is same as active chat id
      if (activeChatId === chatId) {
        // update active chat member role as NOT removed
        // setActiveChatRemovedRoleAsMember(false);
        // update active chat member count in global state
        setActiveChatMemberCount(memberCount);
      }
    } else {
      // Process pinned messages
      // usePinnedMessageState.getState().setPinnedMessageIdsForChat(
      //   chatId,
      //   eventPayload.chat_member.pinned_messages.map((m) => ({
      //     id: m.message_id,
      //     timestamp: m.message.sent_at,
      //   })),
      // );

      // if chat doesnt exist - add a new one in local db
      const newGroupChat: ChatEntity = {
        id: chatId,
        eCSK: eventPayload.chat_member.member.secure_payload,
        currentEpoch: eventPayload.chat_member.chat.current_epoch ?? 0,
        name: eventPayload.chat_member.chat.name,
        hubId: eventPayload.chat_member.chat.hub_id,
        type: ChatType.GROUP, // TO DO => do we assume that this event type is only on group chats ??
        avatar: {
          attachmentId: eventPayload.chat_member.chat.picture,
        },
        timestamp: eventPayload.chat_member.chat.timestamp,
        // TO DO => check if we need  lastSyncedAt on these events
        lastSyncedAt: new Date().toISOString(),

        memberCount: eventPayload.chat_member.chat.member_count,
        unreadCount: 0,
        isMuted: false,
        isPinned: false,
        isArchived: false,
        isPublic: eventPayload.chat_member.chat.is_searchable,
        description: eventPayload.chat_member.chat.description,
        allowThreads: !!eventPayload.chat_member.chat.threads_enabled,
        canPost:
          eventPayload.chat_member.chat.can_post === null
            ? MemberRole.MEMBER
            : eventPayload.chat_member.chat.can_post,

        initStatus: InitChatStatus.SUCCESS,
        roleAsMember: {
          username: memberName,
          role: memberRole,
          removed: false,
        },
        lastMessage: null,
        firstPhaseMsgListInit: false,
        fullMsgListInit: false,
      };

      await addChatToLocalDB(newGroupChat);
    }

    // invalidate the query for the current user's public groups requests
    queryClient.invalidateQueries({
      queryKey: ["public-groups-user-requests"],
    });
    // invalidate the query for the public groups
    queryClient.invalidateQueries({
      queryKey: ["search-public-groups"],
    });
  } else {
    await updateChatInLocalDB(chatId, {
      memberCount: memberCount,
    });

    // if chat id is same as active chat id
    if (activeChatId === chatId) {
      // update active chat member count in global state
      setActiveChatMemberCount(memberCount);
    }
  }

  // invalidate chat members cache
  queryClient.invalidateQueries({
    queryKey: ["chat-members", eventPayload.chat_member.chat.id],
  });

  // invalidate the query for the specific chat's user requests
  queryClient.invalidateQueries({
    queryKey: ["public-group-users-requests", eventPayload.chat_member.chat.id],
  });
};

const onRemoveMemberFromChat = async ({
  eventPayload,
}: {
  eventPayload: ChatMemberEventType;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  const activeChatId = useNavigationState.getState().activeChatId;

  const setActiveChatMemberCount =
    useNavigationState.getState().setActiveChatMemberCount;
  // const setActiveChatRemovedRoleAsMember =
  //   useNavigationState.getState().setActiveChatRemovedRoleAsMember;

  const memberName = eventPayload.chat_member.member.username;
  // const memberRole = eventPayload.chat_member.member.role;
  const chatId = eventPayload.chat_member.chat.id;
  const isCurrentUser = userFingerPrint.username === memberName;

  if (isCurrentUser) {
    // // update chat in local db with new member role as removed
    // await updateChatInLocalDB(chatId, {
    //   roleAsMember: {
    //     username: memberName,
    //     role: memberRole,
    //     removed: true,
    //   },
    // });

    // if chat id is same as active chat id, navigate away via URL (URL-first pattern)
    if (activeChatId === chatId) {
      navigateToChatList();
    }

    // delete messages + attachments (incl. OPFS blobs) + links from local
    // db FIRST, then the chat row — the sweep-before-row order every other
    // teardown site uses (crash between the two must not strand chat data).
    await removeChatDataFromLocalDB(chatId);
    await removeChatFromLocalDB(chatId);

    // invaliudate the query for the current user's requests
    queryClient.invalidateQueries({
      queryKey: ["public-groups-user-requests"],
    });

    // invalidate the admin public chat list so usePublicGroupUsersRequests
    // re-reads from IndexedDB and drops the removed chat
    queryClient.invalidateQueries({
      queryKey: ["admin-user-public-chat"],
    });

    // remove the per-chat requests query entirely to prevent stale background refetches
    queryClient.removeQueries({
      queryKey: ["public-group-users-requests", chatId],
    });
  } else {
    // update chat in local db with new member count
    await updateChatInLocalDB(chatId, {
      memberCount: eventPayload.chat_member.chat.member_count,
    });

    // if chat id is same as active chat id
    if (activeChatId === chatId) {
      // invalidate chat members cache
      queryClient.invalidateQueries({
        queryKey: ["chat-members", activeChatId],
      });
      // update active chat member count in global state
      setActiveChatMemberCount(eventPayload.chat_member.chat.member_count);
    }
  }

  // TO DO => IMPLEMENT CACHE REVALIDATE
  // FOR MEMBERS BY CHAT ID => use React Query
  // MAYBE ALSO CHECK FOR MEMBER ROLES BEFORE REVALIDATION
};
